import { useCallback } from 'react';

import { AvailableSite, useGetFirstAvailableSiteDomain } from '@atlassian/gic-anywhere';

import {
	ErrorStates,
	useBulkCreateContext,
	useSetError,
} from '../providers/BulkCreateContextProvider';

import { useGetJiraProjects } from './useGetJiraProjects';
import { useGetIssueTypes } from './useGetIssueTypes';
import { getPersistedOverrideCloudId } from './localStorageHelpers';
import { useBulkCreateLoggingEvents } from './useBulkCreateLoggingEvents';
import { useSessionData } from '@confluence/session-data';

export const useGetInitialData = () => {
	const [
		{ currentJiraSite, currentJiraProject },
		{ setCurrentJiraSite, setCurrentJiraProject, setCurrentDefaultIssueType },
	] = useBulkCreateContext();
	const { cloudId: sessionCloudId } = useSessionData();

	const persistedOverrideCloudId = getPersistedOverrideCloudId();

	const overrideCloudIdForSitePicker = persistedOverrideCloudId || sessionCloudId;
	const { logRequestFailed } = useBulkCreateLoggingEvents();
	const setError = useSetError();

	const { loading: loadingSites } = useGetFirstAvailableSiteDomain({
		preferredCloudId: overrideCloudIdForSitePicker,
		onComplete: useCallback(
			({ availableSites }) => {
				if (availableSites.length > 0) {
					const firstSite = availableSites[0];
					const preferredSite = availableSites.find(
						(site: AvailableSite) => site.cloudId === overrideCloudIdForSitePicker,
					);
					void setCurrentJiraSite(preferredSite || firstSite);
				}
			},
			[setCurrentJiraSite, overrideCloudIdForSitePicker],
		),
		onError: useCallback(
			(error: Error) => {
				logRequestFailed({ requestName: 'fetch-jira-site', errorMessage: error.message });
				void setError(ErrorStates.SITE_RETRIEVAL);
			},
			[setError, logRequestFailed],
		),
	});

	const { loading: loadingProjects } = useGetJiraProjects({
		cloudId: currentJiraSite?.cloudId,
		query: '',
		onComplete: useCallback(
			(recentProjectData, projectData) => {
				const firstProject = recentProjectData?.[0] || projectData?.[0];
				if (firstProject) {
					void setCurrentJiraProject(firstProject);
				}
			},
			[setCurrentJiraProject],
		),
	});

	const { loading: loadingIssueTypes } = useGetIssueTypes({
		cloudId: currentJiraSite?.cloudId,
		projectId: currentJiraProject?.id,
		onComplete: useCallback(
			(data) => {
				if (data && data.length > 0) {
					void setCurrentDefaultIssueType(data[0]);
				}
			},
			[setCurrentDefaultIssueType],
		),
	});

	return {
		loading: loadingSites || loadingProjects || loadingIssueTypes,
	};
};
